import { MANUAL_CREW_ITEMS, MANUAL_MISC_ITEMS } from "../config/quoteBudgetManualEntryConfig";
import type { ManualEntryArea, ManualEntryContext, ManualEntryVehicleType } from "../types/manualEntry";

/** 住宿快捷模板支持的表头角色。 */
export type ManualStayTemplateRole = "学生" | "老师" | "导游" | "司机";
/** 大巴模板对应原型四个服务入口。 */
export type ManualBusTemplateKind = "local" | "send" | "pick" | "pair";

const ROLE_CODES = { 学生: "student", 老师: "teacher", 导游: "guide", 司机: "driver" } as const;
const ROLE_KEYS = { 学生: "studentCount", 老师: "teacherCount", 导游: "guideCount", 司机: "driverCount" } as const;
const positiveInteger = (value: number | undefined): value is number => Number.isSafeInteger(value) && Number(value) > 0;
const safeName = (name: string) => Boolean(name.trim()) && name.length <= 60 && !/[;；,，、\r\n<>]/u.test(name);

/** 为司导常用项目生成明确角色与零价占位，初始模板和快捷项共用；插入后选中 0 直接改价。 */
export function buildCrewItemTemplate(item: (typeof MANUAL_CREW_ITEMS)[number], context: Pick<ManualEntryContext, "areas" | "dayCount" | "itineraryDays">): string {
  if (item.unit === "人") return `${item.name} ${item.role} 0`;
  const days = [...new Set(context.itineraryDays?.map(day => day.day).filter(positiveInteger) ?? [])].sort((left, right) => left - right);
  const dayCount = positiveInteger(context.dayCount) ? context.dayCount
    : days.length && days.every((day, index) => day === index + 1) ? days.length : undefined;
  const nights = context.areas?.length && context.areas.every(area => Number.isSafeInteger(area.nights) && area.nights >= 0)
    ? context.areas.reduce((sum, area) => sum + area.nights, 0) : undefined;
  // 因素只写数字或占位词，不拼「天/晚/次」单位；按次默认 1，司陪人数单独占位。
  const factor = item.unit === "天" ? positiveInteger(dayCount) ? String(dayCount) : "天数"
    : item.unit === "晚" ? positiveInteger(nights) ? String(nights) : "晚数" : "1";
  return `${item.name} ${item.role} ${item.role === "司陪" ? "人数×" : ""}${factor}×0`;
}

/** 为首次新增草稿生成真实因素与待填价格文本，不创建费用或修改上下文。 */
export function buildManualInitialTemplate(context: ManualEntryContext): string {
  const roles = (Object.keys(ROLE_KEYS) as ManualStayTemplateRole[]).filter(role => positiveInteger(context.people[ROLE_KEYS[role]]));
  const initialRoles: string[] = roles.length ? roles : ["角色"];
  // 飞机/船不录导游票，初始模板与快捷项只保留学生、老师、司机。
  const transportRoles = roles.filter((role) => role !== "导游");
  const transportInitialRoles: string[] = transportRoles.length ? transportRoles : ["角色"];
  const area = context.areas?.find(item => item.id === context.areaId);
  switch (context.category) {
    case "stay":
      return roles.length ? roles.map(role => buildStayRoleTemplate(role, area)).join("\n") : "角色 住宿标准 晚数×0";
    case "point": {
      const items = context.point?.items ?? [];
      const rows = items.flatMap(item => {
        const role = roles.find(candidate => item.travelerRole === candidate || item.travelerRole === ROLE_CODES[candidate]);
        if (!role || !item.skuId || !safeName(item.skuName) || item.skuName.length > 40 || /\s/u.test(item.skuName)) return [];
        const matches = items.filter(other => other.skuName === item.skuName && (!other.travelerRole || other.travelerRole === role || other.travelerRole === ROLE_CODES[role]));
        return matches.length === 1 ? [`#${item.skuName} ${role} 1×0`] : [];
      });
      // 缺候选时与 SKU 快捷一致：角色与次数之间留空格，次数用 1。
      return rows.length ? rows.join("\n") : initialRoles.map(role => `#费用名称 ${role} 1×0`).join("\n");
    }
    case "meal": {
      const areas = context.areaId ? [area] : context.areas?.length ? context.areas : [undefined];
      return areas.flatMap(item => {
        const uniqueName = item && safeName(item.name) && !/\s/u.test(item.name)
          && context.areas?.filter(other => other.name === item.name).length === 1;
        const prefix = context.areaId ? "" : `@${uniqueName ? item.name : "区域"} `;
        const meals = positiveInteger(item?.nights) ? item.nights : "餐数";
        return initialRoles.map(role => `${prefix}${role} 早餐${meals}×0，正餐餐数×0`);
      }).join("\n");
    }
    case "ship":
      return `出发城市-到达城市 ${transportInitialRoles.map(role => `${role} 0`).join("，")}`;
    case "air": {
      const cityFor = (name: string) => {
        const matches = context.cities?.filter(city => city.code && safeName(city.name)
          && (city.name === name || city.name.replace(/市$/u, "") === name || city.aliases?.includes(name))) ?? [];
        return matches.length === 1 ? matches[0] : undefined;
      };
      const routes = new Set((context.transports ?? []).filter(item => item.mode === "air").map(item => {
        const from = cityFor(item.fromName), to = cityFor(item.toName);
        return from && to ? `${from.name}-${to.name}` : "出发城市-到达城市";
      }));
      if (!routes.size) routes.add("出发城市-到达城市");
      return [...routes].map(route => `${route} ${transportInitialRoles.map(role => `${role} 0`).join("，")}`).join("\n");
    }
    case "bus": {
      const days = [...new Set(context.itineraryDays?.map(item => item.day).filter(positiveInteger) ?? [])].sort((left, right) => left - right);
      const runs: Array<Array<{ day: number }>> = [];
      for (const day of days) {
        const current = runs.at(-1);
        if (current && current.at(-1)!.day + 1 === day) current.push({ day });
        else runs.push([{ day }]);
      }
      const vehicles = context.vehicleTypes?.filter(item => item.id && safeName(item.name) && !/\s/u.test(item.name)
        && context.vehicleTypes?.filter(other => other.name === item.name).length === 1);
      return (runs.length ? runs : [[]]).map(run => buildBusTemplate("local", run, vehicles)).join("\n");
    }
    case "crew": {
      const rows = MANUAL_CREW_ITEMS.filter(item => roles.some(role => role === item.role))
        .map(item => buildCrewItemTemplate(item, context));
      return rows.length ? rows.join("\n") : "费用名称 角色 天数×0";
    }
    case "misc":
      return MANUAL_MISC_ITEMS.map(buildMiscItemTemplate).join("\n");
  }
}

/** 模板语法已有独立角色字段；去掉「学生 商务连锁标准」这类同角色空格前缀，避免“学生 学生 …”。 */
function stayStandardLabel(role: ManualStayTemplateRole, name: string): string {
  const trimmed = name.trim();
  if (trimmed.startsWith(`${role} `)) {
    const rest = trimmed.slice(role.length + 1).trim();
    return rest || trimmed;
  }
  return trimmed;
}

/** 根据真实住宿区域生成待填价格模板。 */
export function buildStayRoleTemplate(role: ManualStayTemplateRole, area?: ManualEntryArea): string {
  const standards = area?.standards?.filter(item => safeName(item.name) && (!item.role || item.role === role || item.role === ROLE_CODES[role])) ?? [];
  const unique = standards.filter(item => standards.filter(other => other.name === item.name).length === 1);
  const standard = unique.find(item => item.role === role || item.role === ROLE_CODES[role])
    ?? unique.find(item => !item.role);
  const nights = area && Number.isSafeInteger(area.nights) && area.nights > 0 ? area.nights : "晚数";
  const label = standard ? stayStandardLabel(role, standard.name) : "住宿标准";
  return `${role} ${label} ${nights}×0`;
}

/** 根据真实天次和启用车型生成大巴服务模板。 */
export function buildBusTemplate(kind: ManualBusTemplateKind, itineraryDays?: ManualEntryContext["itineraryDays"], vehicles?: ManualEntryVehicleType[]): string {
  const days = [...new Set(itineraryDays?.map(item => item.day)
    .filter(day => Number.isSafeInteger(day) && day > 0) ?? [])].sort((left, right) => left - right);
  const first = days[0], last = days.at(-1);
  const range = kind === "local" ? days.length > 1 ? `${first}-${last}` : first ?? "范围"
    : kind === "pair" ? days.length > 1 ? `${first},${last}` : "送日,接日"
      : (kind === "send" ? first : last) ?? "日期";
  const service = { local: "当地用车", send: "单送", pick: "单接", pair: "接送" }[kind];
  const vehicle = vehicles?.find(item => item.id && item.name.trim());
  return `${range} ${service} ${buildBusVehicleTemplate(vehicle)}`;
}

/** 返回追加片段，由调用方保留原文并决定分隔符。 */
export function buildBusVehicleTemplate(vehicle?: ManualEntryVehicleType): string {
  return `${vehicle?.id && vehicle.name.trim() ? vehicle.name : "车型"} 1×0`;
}

/** 杂费缺价明确使用单价占位，不能把未填写的价格当作免费。 */
export function buildMiscItemTemplate(item: (typeof MANUAL_MISC_ITEMS)[number]): string {
  return item.role ? `${item.name} ${item.role} 单价` : `${item.name} 单价`;
}

/** 只向光标所在行追加片段，原文保留并返回新光标位置。 */
export function appendManualEntryToCurrentLine(text: string, cursor: number, fragment: string, prefixWhenEmpty = ""): { text: string; cursor: number } {
  const position = Math.max(0, Math.min(text.length, Number.isFinite(cursor) ? cursor : 0));
  if (!fragment) return { text, cursor: position };
  const start = position === 0 ? 0 : text.lastIndexOf("\n", position - 1) + 1;
  const nextLine = text.indexOf("\n", position);
  const end = nextLine < 0 ? text.length : nextLine;
  let current = text.slice(start, end).trimEnd();
  if (/[;；]$/u.test(current)) current = current.slice(0, -1).trimEnd();
  const insertionAt = start + current.length;
  const separator = !current.trim() ? prefixWhenEmpty : /[,，、]$/u.test(current) ? " " : ", ";
  const insertion = separator + fragment;
  return {
    text: text.slice(0, insertionAt) + insertion + text.slice(insertionAt),
    cursor: insertionAt + insertion.length,
  };
}
